import React, { memo, MouseEvent } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Flex } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import clsx from 'clsx';
import useIsFeatureAvailable from 'hooks/useIsFeatureAvailable';
import LazySvg from 'components/LazySvg';
import If from 'components/If';
import { LINKS } from 'utils/constants';
import { SERVICES } from 'utils/service-entitlements-config';
import { getFromBrandConfig } from 'brand-config';
import { useAppSelector } from 'app/hooks';
import { selectBrandCode } from 'services/brand/brandSelectors';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';
import { QaDashboardPage } from 'testdata/constants';
import { useDashboardSummary } from 'contexts/DashboardSummaryContext';
import type { DashboardSummary } from 'services/dashboard/types';
import { MenuItem, NavLinkItemProps } from './types';
import styles from './index.module.scss';

// The sidebar's alerts icon switches between the `alerts` (red-dot)
// and `alertsZero` (no-dot) variants based on whether the user has
// any UNREAD alerts. Sourced from the dashboard summary's top-level
// `alertCounts.unread` field — NOT from the per-category unresolved
// counts. Unresolved counts only drop when an alert is archived, so
// using them kept the red dot visible after the user had read every
// alert. Unread drops the moment an alert is opened, matching the
// expected UX.
function computeUnreadTotal(summary: DashboardSummary | null): number {
  return summary?.alertCounts?.unread ?? 0;
}

function getItem(
  key: React.Key,
  label: React.ReactNode,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group',
  qaSelector?: string,
): MenuItem {
  return {
    key,
    label,
    icon,
    children,
    type,
    'data-qa-selector': qaSelector,
  } as MenuItem;
}

function NavLinkItem(props: NavLinkItemProps) {
  const { navigateTo = '/', label, icon, className = '' } = props;
  const navigate = useNavigate();
  const qaSelector = `sidebar_${label?.toString().toLowerCase().replaceAll(' ', '-')}`;

  // Navlink Item click handler
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (navigateTo === '/dark-web-scan-report') {
      window.open(LINKS.cyexScan, '_blank', 'noopener,noreferrer');
    } else {
      navigate(navigateTo);
    }
  };

  return (
    <NavLink
      to={navigateTo}
      data-qa-selector={qaSelector}
      className={({ isActive }) =>
        clsx(styles.link, {
          [styles.active]: isActive,
          [styles.strokeLink]: !!className,
        })
      }
      onClick={handleClick}
    >
      <LazySvg name={icon} className={styles.menuIcon} />
      {label}
    </NavLink>
  );
}

const MomoizedNavLinkItem = memo(NavLinkItem);

function useSidebarMenuItems() {
  const { summary } = useDashboardSummary();
  const unreadAlertsCount = computeUnreadTotal(summary);

  /* Getting user access to the services based on entitlments */
  const hasAlerts = useIsFeatureAvailable(SERVICES.alerts);
  const hasVpnInTouch = useIsFeatureAvailable(SERVICES.vpnInTouch);
  const hasPasswordDefense = useIsFeatureAvailable(SERVICES.passwordDefense);
  const hasAntivirus = useIsFeatureAvailable(SERVICES.antivirus);
  const hasDboo = useIsFeatureAvailable(SERVICES.dboo);
  const hasPasswords = useIsFeatureAvailable(SERVICES.passwords);
  const hasDigitalVault = useIsFeatureAvailable(SERVICES.digitalVault);
  const hasMedicalDataMonitoring = useIsFeatureAvailable(
    SERVICES.medicalDataMonitoring,
  );
  const hasDarkweb = useIsFeatureAvailable(SERVICES.darkweb);
  const hasSocialMedia = useIsFeatureAvailable(SERVICES.socialMedia);
  const hasDarkwebScanReport = useIsFeatureAvailable(
    SERVICES.darkwebScanReport,
  );
  const hasPasswordScan = useIsFeatureAvailable(SERVICES.passwordScan);
  const hasInsurance = useIsFeatureAvailable(SERVICES.insurance);
  const hasHtmon = useIsFeatureAvailable(SERVICES.htmon);
  const hasPublicRecords = useIsFeatureAvailable(SERVICES.publicRecords);
  const brandCode = useAppSelector(selectBrandCode);
  const planCode = useAppSelector(selectOfferPlanCode);
  const hasTxmonEntitlement = useIsFeatureAvailable(SERVICES.txmon);
  // mshield brand and htitleind/htitlefam plans exclude transaction monitoring nav item
  const TXMON_EXCLUDED_PLANS = ['htitleind', 'htitlefam'];
  const hasTxmon =
    hasTxmonEntitlement &&
    (brandCode as string) !== 'mshield' &&
    !TXMON_EXCLUDED_PLANS.includes(planCode ?? '');
  const hasDownloadsRecommended = getFromBrandConfig('downloadsRecommended');
  const hasCreditScore = useIsFeatureAvailable(SERVICES.creditScore);
  const hasCreditScoreSimulator = useIsFeatureAvailable(
    SERVICES.creditScoreSimulator,
  );
  const hasCreditLock = useIsFeatureAvailable(SERVICES.creditLock);
  const hasCreditReport = useIsFeatureAvailable(SERVICES.creditReport);
  const hasBankAccountMonitoring = useIsFeatureAvailable(
    SERVICES.bankAccountMonitoring,
  );
  const hasWalletProtection = useIsFeatureAvailable(SERVICES.walletProtection);
  const hasFinancialCalculator = useIsFeatureAvailable(
    SERVICES.financialCalculator,
  );

  const menuItems: ItemType[] = [
    getItem(
      'dashboard',
      <MomoizedNavLinkItem
        navigateTo='/dashboard'
        label='Dashboard'
        icon='dashboard'
      />,
    ),
  ];
  const downloadMenuItems: ItemType[] = [];
  const moreFeatureMenuItems: ItemType[] = [];

  if (hasAlerts) {
    menuItems.push(
      getItem(
        'alerts',
        <MomoizedNavLinkItem
          navigateTo='/alerts'
          label='Alerts'
          icon={unreadAlertsCount ? 'alerts' : 'alertsZero'}
        />,
      ),
    );
  }

  /* Download menu items */
  if (hasVpnInTouch) {
    downloadMenuItems.push(
      getItem(
        'vpn-in-touch',
        <MomoizedNavLinkItem
          navigateTo='/vpn-in-touch'
          label='VPN in Touch'
          icon='shield-check'
        />,
      ),
    );
  }
  if (hasPasswordDefense) {
    downloadMenuItems.push(
      getItem(
        'password-defense',
        <MomoizedNavLinkItem
          navigateTo='/password-defense'
          label='Password Defense'
          icon='passwordManager'
        />,
      ),
    );
  }
  if (hasAntivirus) {
    downloadMenuItems.push(
      getItem(
        'antivirus',
        <MomoizedNavLinkItem
          navigateTo='/antivirus'
          label='UltraAV'
          icon='bolt'
        />,
      ),
    );
  }

  /* More features menu items */
  if (hasDboo) {
    moreFeatureMenuItems.push(
      getItem(
        'data-broker-opt-out',
        <MomoizedNavLinkItem
          navigateTo='/data-broker-opt-out'
          label='Data Broker Opt Out'
          icon='dataBrokerOptout'
        />,
      ),
    );
  }
  if (hasPasswords) {
    moreFeatureMenuItems.push(
      getItem(
        'passwords',
        <MomoizedNavLinkItem
          navigateTo='/passwords'
          label='Passwords'
          icon='key'
        />,
      ),
    );
  }
  if (hasDigitalVault) {
    moreFeatureMenuItems.push(
      getItem(
        'digital-vault',
        <MomoizedNavLinkItem
          navigateTo='/digital-vault'
          label='Digital Vault'
          icon='digitalVault'
        />,
      ),
    );
  }
  if (hasMedicalDataMonitoring) {
    moreFeatureMenuItems.push(
      getItem(
        'medical-data-monitoring',
        <NavLinkItem
          navigateTo='/medical-data-monitoring'
          label='Medical Data Monitoring'
          icon='medical-square'
          className='strokeLink'
        />,
      ),
    );
  }
  if (hasDarkweb) {
    moreFeatureMenuItems.push(
      getItem(
        'dark-web',
        <MomoizedNavLinkItem
          navigateTo='/dark-web'
          label='Dark Web Watchlist'
          icon='global-search'
        />,
      ),
    );
  }
  if (hasDarkwebScanReport) {
    moreFeatureMenuItems.push(
      getItem(
        'dark-web-scan-report',
        <MomoizedNavLinkItem
          navigateTo='/dark-web-scan-report'
          label={
            <>
              Dark Web Scan Report <LazySvg name='arrow-up-right' />
            </>
          }
          icon='darkweb-scan-report'
        />,
      ),
    );
  }
  if (hasPasswordScan) {
    moreFeatureMenuItems.push(
      getItem(
        'password-scan',
        <MomoizedNavLinkItem
          navigateTo='/password-scan'
          label='Password Scan'
          icon='passwordScan'
        />,
      ),
    );
  }
  if (hasSocialMedia) {
    moreFeatureMenuItems.push(
      getItem(
        'social-media-monitoring',
        <MomoizedNavLinkItem
          navigateTo='/social-media-monitoring'
          label='Social Media Monitoring'
          icon='social_media_protection'
        />,
      ),
    );
  }
  if (hasInsurance) {
    moreFeatureMenuItems.push(
      getItem(
        'insurance',
        <MomoizedNavLinkItem
          navigateTo='/insurance'
          label='Insurance'
          icon='insurance-shape'
        />,
      ),
    );
  }
  if (hasHtmon) {
    moreFeatureMenuItems.push(
      getItem(
        'home-title-monitoring',
        <MomoizedNavLinkItem
          navigateTo='/financial/new-home-title-monitoring'
          label='Home Title Monitoring'
          icon='home-title'
        />,
      ),
    );
  }

  if (hasPublicRecords) {
    moreFeatureMenuItems.push(
      getItem(
        'public-records-monitoring',
        <MomoizedNavLinkItem
          navigateTo='/financial/home-title-monitoring'
          label='Home Title Monitoring'
          icon='home-title'
        />,
      ),
    );
  }

  if (hasTxmon) {
    moreFeatureMenuItems.push(
      getItem(
        'financial/transaction-monitoring',
        <MomoizedNavLinkItem
          navigateTo='/financial/transaction-monitoring'
          label='Transaction Monitoring'
          icon='alert-transaction-monitor'
        />,
      ),
    );
  }
  if (hasCreditScore) {
    moreFeatureMenuItems.push(
      getItem(
        'credit-score',
        <MomoizedNavLinkItem
          navigateTo='/credit-score'
          label='Credit Score'
          icon='credit-score'
        />,
      ),
    );
  }
  if (hasCreditScore) {
    moreFeatureMenuItems.push(
      getItem(
        'credit-tracker',
        <MomoizedNavLinkItem
          navigateTo='/credit-tracker'
          label='Credit Tracker'
          icon='credit-tracker'
        />,
      ),
    );
  }
  if (hasCreditLock) {
    moreFeatureMenuItems.push(
      getItem(
        'credit-lock',
        <MomoizedNavLinkItem
          navigateTo='/credit-lock'
          label='Credit Lock'
          icon='credit-lock'
        />,
      ),
    );
  }
  if (hasWalletProtection) {
    moreFeatureMenuItems.push(
      getItem(
        'wallet-protection',
        <MomoizedNavLinkItem
          navigateTo='/wallet-protection'
          label='Wallet Protection'
          icon='wallet'
        />,
      ),
    );
  }
  if (hasCreditScoreSimulator) {
    moreFeatureMenuItems.push(
      getItem(
        'credit-score-simulator',
        <MomoizedNavLinkItem
          navigateTo='/credit/score-simulator'
          label='Credit Score Simulator'
          icon='credit-score-simulator'
        />,
      ),
    );
  }
  if (hasCreditReport) {
    moreFeatureMenuItems.push(
      getItem(
        'credit-report',
        <MomoizedNavLinkItem
          navigateTo='/credit-report'
          label='Credit Report'
          icon='credit-report'
        />,
      ),
    );
  }

  if (hasBankAccountMonitoring) {
    moreFeatureMenuItems.push(
      getItem(
        'bank-account-monitoring',
        <MomoizedNavLinkItem
          navigateTo='/bank-account-monitoring'
          label='Bank Account Monitoring'
          icon='bank'
        />,
      ),
    );
  }

  if (hasFinancialCalculator) {
    moreFeatureMenuItems.push(
      getItem(
        'financial-calculator',
        <MomoizedNavLinkItem
          navigateTo='/financial/financial-calculator'
          label='Financial Calculator'
          icon='financial-calculator'
          className='strokeLink'
        />,
      ),
    );
  }

  /* Pushing download menu items into the sidebar menu items if user has access to atlease one of them */
  if (downloadMenuItems.length) {
    menuItems.push(
      getItem(
        'downloads',
        <Flex align='center' justify='space-between' className={styles.link}>
          <strong className={styles.downloads}>Downloads</strong>
          <If condition={hasDownloadsRecommended}>
            <div className={styles.recommendedBadge}>Recommended</div>
          </If>
        </Flex>,
        null,
        downloadMenuItems,
        'group',
      ),
    );
  }

  /* Pushing more feature menu items into the sidebar menu items if user has access to atlease one of them */
  if (moreFeatureMenuItems.length) {
    menuItems.push(
      getItem(
        'moreFeatures',
        'More features',
        null,
        moreFeatureMenuItems,
        undefined,
        QaDashboardPage.dropdownMoreFeatures,
      ),
    );
  }

  return menuItems;
}

export default useSidebarMenuItems;
